'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

import { canonicalHref, isSamePath } from '@/utils/href'

/**
 * Upgrades plain same-origin `<a>` clicks to client-side (soft) navigations.
 *
 * Rich content rendered from the CMS via `html-react-parser` produces real `<a>`
 * tags, not `<Link>` components, so Next never intercepts them — every such click
 * is a full-page reload (re-download all chunks + re-hydrate). This single
 * document-level handler catches those clicks and routes them through the App
 * Router instead, the same way `<Link>` would.
 *
 * It defers to the browser (does nothing) for anything that must not become a
 * soft nav: clicks `<Link>` already handled, modified/non-left clicks,
 * new-tab/window targets, downloads, external origins, non-http schemes
 * (`mailto:`/`tel:`), in-page hashes, static files, and elements that opt out via
 * `data-no-spa`, `data-bs-toggle`, or `role="button"`.
 */
export function useInternalLinkNavigation() {
  const router = useRouter()

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      // A <Link> (or any other handler) already took over this click.
      if (event.defaultPrevented) return
      // Only plain left-clicks — let the browser open new tabs / save targets.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return

      const anchor = (event.target as Element | null)?.closest?.('a')
      if (!anchor) return

      // Opt-outs and non-navigational anchors (Bootstrap toggles, downloads, …).
      if (
        anchor.hasAttribute('download') ||
        anchor.hasAttribute('data-no-spa') ||
        anchor.hasAttribute('data-bs-toggle') ||
        anchor.getAttribute('role') === 'button' ||
        (anchor.getAttribute('target') && anchor.getAttribute('target') !== '_self') ||
        /\bexternal\b/.test(anchor.getAttribute('rel') || '')
      )
        return

      const rawHref = anchor.getAttribute('href')
      if (!rawHref || rawHref.startsWith('#')) return

      let url: URL
      try {
        url = new URL(anchor.href, window.location.href)
      } catch {
        return
      }

      // External origin or non-http scheme (mailto:, tel:, …).
      if (url.origin !== window.location.origin) return
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return
      // In-page hash on the current route — leave native smooth scrolling alone.
      if (isSamePath(url.pathname, window.location.pathname) && url.hash) return

      // Static files (last segment has an extension) — let the browser fetch them.
      const lastSegment = url.pathname.slice(url.pathname.lastIndexOf('/') + 1)
      if (lastSegment.includes('.')) return

      event.preventDefault()
      router.push(canonicalHref(url.pathname + url.search + url.hash))
    }

    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [router])
}
